// Asking the second thread to enumerate, from the first one.
//
// The panel wants three things the bare worker does not give: a promise, the
// tree as it is discovered rather than only at the end, and a way to give up.
// Each enumeration gets a worker of its own — the walk is a tight loop that
// never yields, so two sharing one would just mean the second waits.
//
// Falls back to running on this thread where there is no `Worker` at all (a
// test, a Node script). That is a freeze rather than a wait — which is exactly
// what the browser used to do — but it keeps the answer the same, and nothing in
// Node has a frame to drop.

import { exploreOutcomes } from "./explore.js";
import type { ExploreNode, ExploreResult, Progress } from "./explore.js";
import type { Fix, WorkerReply, WorkerRequest } from "./worker.js";
import type { RunParams } from "./race-oracle.js";

/** Whether this environment can run programs off the main thread. */
export function workersAvailable(): boolean {
  return typeof Worker !== "undefined" && typeof URL !== "undefined";
}

/** The bundle the worker entry builds to, a sibling of the main one. */
function spawn(): Worker {
  return new Worker(new URL("./worker.js", import.meta.url), { type: "module" });
}

// ---------------------------------------------------------------------------
// The enumeration
// ---------------------------------------------------------------------------

/** A running enumeration: what it will produce, and how to stop it. */
export interface Exploration {
  /** Resolves with the enumeration, or rejects — including when cancelled, with
   *  `CANCELLED` as the message. */
  result: Promise<ExploreResult>;
  /** Stop now. The promise rejects; nothing further is reported. */
  cancel(): void;
}

/** The rejection a cancelled exploration produces, so a caller can tell "the
 *  user stopped it" from "it broke". */
export const CANCELLED = "exploration cancelled";

export interface ExploreClientOptions {
  budget?: number;
  /** Walk everything, rather than stopping once an assertion has been seen to
   *  both hold and fail. */
  exhaustive?: boolean;
  /** The walk so far, and the nodes found since the last call. */
  onProgress?: (p: Progress, nodes: ExploreNode[]) => void;
}

/**
 * Enumerate the outcomes of `source` in a worker of its own, or inline where
 * there is none.
 */
export function exploreInWorker(
  source: string,
  params: RunParams,
  options: ExploreClientOptions = {}
): Exploration {
  if (!workersAvailable()) {
    // No worker: do it here. The caller still gets a promise, and still gets
    // progress — it simply arrives all at once, at the end, because nothing else
    // could have run in between anyway.
    try {
      return { result: Promise.resolve(exploreOutcomes(source, params, options)), cancel: () => {} };
    } catch (e) {
      return { result: Promise.reject(e), cancel: () => {} };
    }
  }

  const worker = spawn();
  let settled = false;
  let stop: (why: Error) => void = () => {};
  const result = new Promise<ExploreResult>((resolve, reject) => {
    stop = reject;
    worker.onmessage = (event: MessageEvent<WorkerReply>): void => {
      const reply = event.data;
      if (reply.kind === "progress") {
        options.onProgress?.(reply.progress, reply.nodes);
        return;
      }
      settled = true;
      worker.terminate();
      if (reply.kind === "explore") resolve(reply.result);
      else reject(new Error(reply.kind === "error" ? reply.message : "unexpected reply"));
    };
    worker.onerror = (event): void => {
      settled = true;
      worker.terminate();
      reject(new Error(event.message || "the exploration worker failed"));
    };
    const request: WorkerRequest = {
      kind: "explore",
      id: 1,
      source,
      params,
      budget: options.budget,
      exhaustive: options.exhaustive,
    };
    worker.postMessage(request);
  });

  return {
    result,
    cancel: () => {
      if (settled) return;
      settled = true;
      worker.terminate();
      stop(new Error(CANCELLED));
    },
  };
}

// ---------------------------------------------------------------------------
// The fix
// ---------------------------------------------------------------------------

/**
 * The `txn` that removes the race in `schedule`, worked out off the main thread.
 *
 * Asked only once the enumeration has found a race, because it is another few
 * dozen executions: the enumeration says *that* the program races, and this says
 * which two statements were meant to be atomic — and then checks, by running the
 * patched program, that wrapping them leaves one outcome.
 *
 * Resolves to null when there is nothing in the user's own program to wrap (the
 * pair is in library code), and rejects only if the request itself broke.
 */
export function fixInWorker(source: string, params: RunParams, schedule: number[]): Promise<Fix | null> {
  if (!workersAvailable()) {
    // No worker: this is seconds of running, so on the main thread it is a
    // freeze. Nothing in Node asks for it, so nothing here has to.
    return Promise.resolve(null);
  }
  const worker = spawn();
  return new Promise<Fix | null>((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<WorkerReply>): void => {
      const reply = event.data;
      worker.terminate();
      if (reply.kind === "fix") resolve(reply.fix);
      else reject(new Error(reply.kind === "error" ? reply.message : "unexpected reply"));
    };
    worker.onerror = (event): void => {
      worker.terminate();
      reject(new Error(event.message || "the fix worker failed"));
    };
    const request: WorkerRequest = { kind: "fix", id: 1, source, params, schedule };
    worker.postMessage(request);
  });
}
